import styled, { css } from 'styled-components';
import { Button } from 'components';
import { theme, toRem } from 'design';

export type InputHeightOptions = 'large' | 'small';
export type PickerPositionOptions = 'left' | 'right';

interface IUIProps {
  fluidWidth?: boolean;
  hasPresets?: boolean;
  inputFocused?: boolean;
  inputHeight?: InputHeightOptions;
  pickerPosition?: PickerPositionOptions;
  pickerVisible?: boolean;
  width?: number;
}

export const Layout = styled.div<IUIProps>`
  position: relative;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: auto;
  height: auto;

  ${({ fluidWidth }) =>
    fluidWidth &&
    css`
      width: 100%;
    `}

  ${({ width }) =>
    width &&
    css`
      width: ${toRem(width)};
    `}
`;

const WIDTH_ICON_CONTAINER = 35.75;

export const InputWrap = styled.div<IUIProps>`
  position: relative;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  height: ${toRem(theme.input.height.large)};
  padding: 0 ${toRem(WIDTH_ICON_CONTAINER)} 0 ${toRem(11)};
  border-radius: ${toRem(5.5)};
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  background-color: ${theme.color.white};
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.regular};
  font-size: ${toRem(13.25)};
  line-height: 100%;
  color: ${theme.color.neutral[900]};

  &:hover {
    border-color: ${theme.color.neutral[400]};
    background-color: ${theme.color.white};
  }

  &:focus {
    border-color: ${theme.color.neutral[400]};
  }

  ${({ inputFocused }) =>
    inputFocused &&
    css`
      border-color: ${theme.color.neutral[400]};
    `}

  ${({ inputHeight }) =>
    inputHeight === 'small' &&
    css`
      height: ${toRem(theme.input.height.small)};
    `}

  ${({ pickerVisible }) =>
    pickerVisible &&
    css`
      z-index: ${theme.zIndex.overlay + 2};

      &:hover,
      &:focus {
        border-color: ${theme.color.neutral[400]};
        background-color: ${theme.color.white};
      }
    `}
`;

const Input = styled.input.attrs({ type: 'text' })<IUIProps>`
  display: flex;
  padding: 5px;
  border: none;
  background: transparent;
  outline: none;
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.regular};
  font-size: ${toRem(13.25)};
  color: ${theme.color.neutral[900]};
  text-align: center;
  cursor: pointer;

  @media (min-width: ${theme.breakpoint.med}px) {
    max-width: ${toRem(88)};

    ${({ fluidWidth }) =>
      fluidWidth &&
      css`
        max-width: none;
      `}
  }

  &::placeholder {
    color: ${theme.color.neutral[400]};
  }

  &.hasFocus {
    border-radius: 5px;
    color: ${theme.color.white};
    background-color: ${theme.color.secondary[400]} !important;
    outline: none;
  }
`;

export const InputStart = styled(Input)`
  min-width: ${toRem(88)};
`;

export const InputEnd = styled(Input)`
  min-width: ${toRem(88)};
`;

export const InputDivider = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  width: auto;
  height: 100%;
  padding: ${toRem(1)} ${toRem(11)} 0;
  text-align: center;
  color: ${theme.color.neutral[400]};
`;

export const InputWrapSubmit = styled.button.attrs({
  type: 'button',
})`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${toRem(WIDTH_ICON_CONTAINER)};
  height: 100%;

  svg {
    width: ${toRem(13.75)};
    height: ${toRem(14)};
    fill: ${theme.color.neutral[900]};
    transition: fill ${theme.transition.icon};
  }

  &:hover,
  &:focus {
    svg {
      fill: ${theme.color.neutral[700]};
    }
  }

  &:disabled {
    cursor: not-allowed;

    svg {
      fill: ${theme.color.neutral[400]};
    }
  }
`;

const PICKER_OFFSET = 3;

export const PickerContainer = styled.section<IUIProps>`
  position: absolute;
  top: ${toRem(theme.input.height.large + PICKER_OFFSET)};
  right: 0;
  z-index: ${theme.zIndex.overlay + 1};
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: auto;
  height: auto;
  border-radius: ${toRem(5.5)};
  background-color: ${theme.color.white};
  box-shadow: ${theme.shadow.dialog};
  overflow: hidden;

  ${({ inputHeight }) =>
    inputHeight === 'small' &&
    css`
      top: ${toRem(theme.input.height.large + PICKER_OFFSET)};
    `}

  ${({ pickerPosition }) =>
    pickerPosition === 'left' &&
    css`
      @media (min-width: ${theme.breakpoint.med}px) {
        right: auto;
        left: 0;
      }
    `}
`;

export const CloseOverlay = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: ${theme.zIndex.overlay};
  background: transparent;
`;

export const PickerFooter = styled.footer<IUIProps>`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  height: ${toRem(44)};
  padding: 0 ${toRem(11)};
  border-top: ${toRem(1)} solid ${theme.color.neutral[200]};

  ${({ hasPresets }) =>
    hasPresets &&
    css`
      justify-content: space-between;
    `}
`;

export const PickerFooterPresets = styled.div`
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
`;

const PickerFooterButtonStyles = css`
  display: flex;
  justify-content: center;
  align-items: center;
  width: auto;
  height: ${toRem(22)};
  padding: 0 ${toRem(8.75)};
  border-radius: ${toRem(2.75)};
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.medium};
  font-size: ${toRem(11)};
  line-height: 100%;
  transition: background-color ${theme.transition.button.fast};
`;

export const PresetButton = styled.button.attrs({
  type: 'button',
})`
  ${PickerFooterButtonStyles}
  margin-right: ${toRem(5.5)};
  background-color: ${theme.color.neutral[200]};
  color: ${theme.color.neutral[800]};
  white-space: nowrap;

  &:hover {
    background-color: ${theme.color.secondary[400]};
    color: ${theme.color.white};
  }
`;

export const SubmitButton = styled(Button)`
  ${PickerFooterButtonStyles}
  background-color: ${theme.color.primary[500]};
  font-weight: ${theme.font.weight.semiBold};
  color: ${theme.color.white};
  text-transform: capitalize;

  &:hover {
    background-color: ${theme.color.primary[600]};
  }

  &:disabled {
    background-color: ${theme.color.neutral[200]};
    color: ${theme.color.neutral[800]};
    cursor: not-allowed;

    &:hover {
      background-color: ${theme.color.neutral[200]};
      color: ${theme.color.neutral[800]};
    }
  }
`;
